import Form from "components/form/Form";
import { Box } from "@mui/material";
import { useForm } from "react-hook-form";
import {
  Competency_Map_Status,
  OrderType,
  PlatformCompetencyMapListOrderFields,
  useCompetencyMapOptionsQuery,
  useExaminationInfoQuery,
} from "@generated/graphql";
import { useParams } from "react-router-dom";
import CircularLoading from "components/CircularLoading";
import { useContext } from "react";
import useIsCreate from "hooks/useIsCreate";
import ButtonWithPermissions, { generateUnavailableText } from "components/buttons/ButtonWithPermissions";
import { CompanyContext } from "context/companyContext";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import typography from "constants/typography.constants";
import InfoBlock from "../../components/InfoBlock";
import { ExaminationType, GroupAndOpenInfoInputs } from "../types";
import GroupAndOpenLeft from "./Left";
import GroupAndOpenRight from "./Right";
import useInitBaseExaminationForm from "../hooks/useInitExaminationForm";
import useCreateExamination from "../../hooks/useCreateExamination";
import { getExaminationSendResultType } from "../../helpers";

interface ExaminationContentProps {
  examinationType: ExaminationType;
}

const ExaminationContent = ({ examinationType }: ExaminationContentProps) => {
  const { id } = useParams();
  const isCreate = useIsCreate();
  const { addToast } = useContext(ToastContext);
  const { data: competencyMapsData, loading: competencyMapsLoading } = useCompetencyMapOptionsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      order: {
        field: PlatformCompetencyMapListOrderFields.CreatedAt,
        order: OrderType.Desc,
      },
      filter: {
        status: Competency_Map_Status.Approved,
      },
    },
  });
  const { createExaminationLoading, onCreateExamination } = useCreateExamination();

  const competencyMaps = competencyMapsData?.competencyMaps.data;

  const { data, loading } = useExaminationInfoQuery({
    variables: {
      examinationId: id!,
    },
    skip: !id,
  });

  const initFormData = useInitBaseExaminationForm(data?.examination, competencyMaps);

  const form = useForm<GroupAndOpenInfoInputs>({ values: initFormData });
  const { handleSubmit } = form;

  const onSubmit = (formData: GroupAndOpenInfoInputs | GroupAndOpenInfoInputs) => {
    if (formData.competencyMap?.id) {
      onCreateExamination({
        competencyMapId: formData.competencyMap.id,
        title: formData.title || formData.competencyMap.title,
        isSendResult: formData.isSendResult,
        sendResultType: getExaminationSendResultType(formData.isExtendedReport),
      });
    } else {
      addToast({ type: ToastTypeEnum.ERROR, text: typography.errors.pleaseRepeat });
      console.error("onGroupSubmit: Не указан formData.competencyMap.id");
    }
  };

  const { isCreateAvailable, currentCompany } = useContext(CompanyContext);

  if (loading) return <CircularLoading />;

  return (
    <Box sx={{ py: 2, px: 2.5, backgroundColor: "white", borderRadius: 2, width: "100%", marginBottom: 2.5 }}>
      <Form form={form}>
        <InfoBlock
          LeftElement={<GroupAndOpenLeft isCreate={isCreate} />}
          RightElement={
            <GroupAndOpenRight
              form={form}
              examinationType={examinationType}
              competencyMaps={competencyMaps}
              competencyMapsLoading={competencyMapsLoading}
            />
          }
          ControlsElement={
            isCreate ? (
              <ButtonWithPermissions
                buttonText='Сохранить'
                onClick={handleSubmit(onSubmit)}
                tooltipText={generateUnavailableText(isCreateAvailable, currentCompany, "создания")}
                loading={loading || createExaminationLoading}
              />
            ) : undefined
          }
        />
      </Form>
    </Box>
  );
};

export default ExaminationContent;
